import { X } from "lucide-react";

interface QuestionResponse {
  id: number;
  date: string;
  questions: {
    question: string;
    response: string | number;
    section?: string;
  }[];
  responder: string; // Add responder
  summary?: Record<string, string>;
}

interface RawDataDisplayProps {
  data: QuestionResponse[];
  measureName: string;
  selectedIds?: number[];
  onClearSelection?: () => void;
}

export function RawDataDisplay({
  data,
  // measureName: _measureName,
  selectedIds = [],
  onClearSelection,
}: RawDataDisplayProps) {
  // Filter data to show only selected IDs, or all if no selection
  const displayData =
    selectedIds.length > 0
      ? data.filter((assessment) => selectedIds.includes(assessment.id))
      : data;

  // Sorting displayData to match selectedIds order for consistency
  const sortedDisplayData = [...displayData].sort((a, b) => {
    return selectedIds.indexOf(a.id) - selectedIds.indexOf(b.id);
  });

  const isComparing =
    selectedIds.length === 2 && sortedDisplayData.length === 2;

  /** Splits a flat questions array into sections.
   *  Questions with `section` set are grouped under that section header.
   *  Questions without `section` are grouped under "Questions". */
  const splitIntoSections = (
    questions: QuestionResponse["questions"],
  ): { sectionName: string; questions: QuestionResponse["questions"] }[] => {
    const sections: {
      sectionName: string;
      questions: QuestionResponse["questions"];
    }[] = [];
    let currentSection: string | null = null;
    let currentItems: QuestionResponse["questions"] = [];

    for (const q of questions) {
      const sectionLabel = q.section ?? null;
      if (sectionLabel !== currentSection) {
        if (currentItems.length > 0) {
          sections.push({
            sectionName: currentSection ?? "Questions",
            questions: currentItems,
          });
        }
        currentSection = sectionLabel;
        currentItems = [q];
      } else {
        currentItems.push(q);
      }
    }
    if (currentItems.length > 0) {
      sections.push({
        sectionName: currentSection ?? "Questions",
        questions: currentItems,
      });
    }
    return sections;
  };

  return (
    <div
      className="w-full bg-white p-6 rounded-lg border mt-6"
      style={{ borderColor: "#E2E8F0" }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold" style={{ color: "#1A202C" }}>
          {isComparing
            ? "Assessment Comparison"
            : "Raw Data - Question Responses"}
        </h3>

        {selectedIds.length > 0 && (
          <button
            onClick={onClearSelection}
            className="flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors hover:bg-gray-100"
            style={{ color: "#475569" }}
          >
            <X size={16} />
            Clear Selection
          </button>
        )}
      </div>

      {selectedIds.length > 0 && sortedDisplayData.length === 0 && (
        <div className="text-center py-8" style={{ color: "#475569" }}>
          <p className="text-sm">No raw data available for selected IDs</p>
          <button
            onClick={onClearSelection}
            className="mt-3 px-4 py-2 rounded text-sm font-medium text-white"
            style={{ backgroundColor: "#5A8DAB" }}
          >
            Show All Data
          </button>
        </div>
      )}

      {isComparing ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b" style={{ borderColor: "#E2E8F0" }}>
                <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 w-1/2">
                  Question
                </th>
                <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: "#F59E0B" }}
                    ></span>
                    {sortedDisplayData[0].date}{" "}
                    {sortedDisplayData[0].responder &&
                      `- ${sortedDisplayData[0].responder}`}
                  </div>
                </th>
                <th className="py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: "#8B5CF6" }}
                    ></span>
                    {sortedDisplayData[1].date}{" "}
                    {sortedDisplayData[1].responder &&
                      `- ${sortedDisplayData[1].responder}`}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const sections = splitIntoSections(
                  sortedDisplayData[0].questions,
                );
                let globalIdx = 0;
                return sections.map((section) => (
                  <>
                    {section.sectionName !== "Questions" && (
                      <tr key={`section-${section.sectionName}`}>
                        <td
                          colSpan={3}
                          className="py-2 px-4 text-xs font-bold uppercase tracking-widest bg-slate-100 text-slate-500 border-t border-b"
                          style={{ borderColor: "#E2E8F0" }}
                        >
                          {section.sectionName}
                        </td>
                      </tr>
                    )}
                    {section.questions.map((q1) => {
                      const currentIdx = globalIdx++;
                      const q2 = sortedDisplayData[1].questions[currentIdx];
                      const hasChanged = q2 && q1.response !== q2.response;
                      const r1 = Number(q1.response);
                      const r2 = Number(q2?.response);
                      const isNumericChange =
                        hasChanged &&
                        !isNaN(r1) &&
                        !isNaN(r2) &&
                        q2 !== undefined;
                      const changeDirection = isNumericChange
                        ? r2 > r1
                          ? "increased"
                          : "decreased"
                        : "changed";

                      return (
                        <tr
                          key={currentIdx}
                          className={`border-b group transition-colors ${hasChanged ? "bg-amber-50/30" : "hover:bg-slate-50"}`}
                          style={{ borderColor: "#F1F5F9" }}
                        >
                          <td className="py-4 px-4 align-top">
                            <div className="text-sm font-medium text-slate-800">
                              <span className="text-slate-400 mr-2">
                                Q{currentIdx + 1}
                              </span>
                              {q1.question}
                            </div>
                          </td>
                          <td className="py-4 px-4 align-top">
                            <div className="text-sm text-slate-700 font-medium">
                              {q1.response}
                            </div>
                          </td>
                          <td className="py-4 px-4 align-top">
                            <div className="flex items-center gap-2">
                              <div
                                className={`text-sm font-bold ${hasChanged ? "text-amber-700 bg-amber-100 px-2 py-0.5 rounded" : "text-slate-700 font-medium"}`}
                              >
                                {q2?.response ?? "—"}
                              </div>
                              {hasChanged && (
                                <div
                                  className={`flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm border ${
                                    changeDirection === "increased"
                                      ? "text-red-600 bg-white border-red-200"
                                      : changeDirection === "decreased"
                                        ? "text-green-600 bg-white border-green-200"
                                        : "text-amber-600 bg-white border-amber-200"
                                  }`}
                                >
                                  {changeDirection === "increased"
                                    ? "INCREASED"
                                    : changeDirection === "decreased"
                                      ? "DECREASED"
                                      : "CHANGED"}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </>
                ));
              })()}
            </tbody>
          </table>
          <div className="mt-6 p-4 rounded-lg bg-slate-50 border border-slate-200">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
              Comparison Summary
            </h4>
            <p className="text-sm text-slate-600">
              Comparing {sortedDisplayData[0].date} (Basis) vs{" "}
              {sortedDisplayData[1].date} (Comparison). Total of{" "}
              {
                sortedDisplayData[0].questions.filter(
                  (q, i) =>
                    q.response !== sortedDisplayData[1].questions[i]?.response,
                ).length
              }{" "}
              response differences identified.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedDisplayData.map((assessment, index) => {
            const isSelected = selectedIds.includes(assessment.id);
            const selectionIndex = selectedIds.indexOf(assessment.id);
            const sections = splitIntoSections(assessment.questions);

            return (
              <div
                key={index}
                className={`border-b pb-6 last:border-b-0 ${isSelected ? "bg-slate-50 -m-2 p-4 rounded-lg border-2" : ""}`}
                style={{
                  borderColor: isSelected
                    ? selectionIndex === 0
                      ? "#F59E0B"
                      : "#8B5CF6"
                    : "#E2E8F0",
                }}
              >
                <div
                  className="font-bold mb-3 flex items-center justify-between"
                  style={{ color: "#5A8DAB" }}
                >
                  <div className="flex items-center gap-2">
                    Assessment Date: {assessment.date}{" "}
                    {assessment.responder && `- ${assessment.responder}`}
                    {isSelected && (
                      <span
                        className="text-xs px-2 py-0.5 rounded text-white"
                        style={{
                          backgroundColor:
                            selectionIndex === 0 ? "#F59E0B" : "#8B5CF6",
                        }}
                      >
                        {selectionIndex === 0
                          ? "Primary Selection"
                          : "Secondary Selection"}
                      </span>
                    )}
                  </div>
                  {!isSelected && selectedIds.length < 2 && (
                    <button
                      /* v8 ignore next 3 */ onClick={() =>
                        selectedIds.length === 0 ? onClearSelection?.() : null
                      } // This is handled by parent, but let's just show intent
                      className="text-xs font-medium text-slate-400 hover:text-blue-500 underline"
                    >
                      Click point on chart to compare
                    </button>
                  )}
                </div>

                {/* Render questions grouped by section */}
                {sections.map((section, sIdx) => {
                  // Calculate the global question offset for this section
                  const offset = sections
                    .slice(0, sIdx)
                    .reduce((acc, s) => acc + s.questions.length, 0);

                  return (
                    <div key={sIdx} className="mb-4 last:mb-0">
                      {/* Section header — only shown for non-default sections */}
                      {section.sectionName !== "Questions" && (
                        <div className="flex items-center gap-2 mb-2 mt-4">
                          <div className="h-px flex-1 bg-slate-200" />
                          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-2">
                            {section.sectionName}
                          </span>
                          <div className="h-px flex-1 bg-slate-200" />
                        </div>
                      )}

                      <div className="space-y-3">
                        {section.questions.map((item, qIndex) => (
                          <div
                            key={qIndex}
                            className="grid grid-cols-[1fr_200px] gap-4 items-start"
                          >
                            <div
                              className="text-sm"
                              style={{ color: "#1A202C" }}
                            >
                              <span
                                className="font-medium"
                                style={{ color: "#475569" }}
                              >
                                Q{offset + qIndex + 1}:
                              </span>{" "}
                              {item.question}
                            </div>
                            <div
                              className="text-sm font-medium text-right"
                              style={{ color: "#1A202C" }}
                            >
                              {item.response}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
